import { useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import { Globe, Server, Plus, Loader2, Radar, Pencil } from "lucide-react";
import { toast } from "sonner";

function OriginBadge({ source }) {
  const isDiscovery = source === "asset_discovery";
  return (
    <span
      data-testid={`origin-badge-${isDiscovery ? "discovery" : "manual"}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-widest",
        isDiscovery
          ? "border-primary/50 text-primary bg-primary/10"
          : "border-border text-muted-foreground bg-muted/20"
      )}
    >
      {isDiscovery ? <Radar className="w-2.5 h-2.5" /> : <Pencil className="w-2.5 h-2.5" />}
      {isDiscovery ? "descubierto" : "manual"}
    </span>
  );
}

/**
 * "Registered assets" card — root domains + IP ranges + add-asset form.
 * Owns its own "add asset" input state; delegates Shodan scans to parent.
 */
export function ClientAssetsCard({ client, clientId, onAssetAdded, onShodanScan, shodanBusy }) {
  const [assetKind, setAssetKind] = useState("domain");
  const [assetVal, setAssetVal] = useState("");
  const [busy, setBusy] = useState(false);

  const addAsset = async (e) => {
    e.preventDefault();
    if (!assetVal.trim()) return;
    setBusy(true);
    try {
      const url = assetKind === "domain"
        ? `/clients/${clientId}/assets/domain`
        : `/clients/${clientId}/assets/ip`;
      await api.post(url, { value: assetVal.trim() });
      toast.success(`${assetKind === "domain" ? "Dominio" : "IP"} añadido`);
      setAssetVal("");
      onAssetAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo añadir el activo");
    } finally { setBusy(false); }
  };

  return (
    <section className="lg:col-span-2 border border-border bg-card/60 p-5 space-y-4">
      <div>
        <div className="overline">Inventario de superficie de ataque</div>
        <h2 className="font-display text-lg font-medium mt-1">Activos registrados</h2>
      </div>

      <div className="border border-border bg-background">
        <div className="p-4 border-b border-border">
          <div className="overline mb-2">Dominios raíz ({client.domains?.length || 0})</div>
          {client.domains?.length ? (
            <div className="flex flex-wrap gap-2">
              {client.domains.map((d) => (
                <span key={d.id} className="font-mono text-xs border border-border px-2 py-1 flex items-center gap-2" data-testid={`domain-${d.id}`}>
                  <Globe className="w-3 h-3 text-primary" />{d.name}
                  <OriginBadge source={d.source} />
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Aún no hay dominios.</div>
          )}
        </div>
        <div className="p-4">
          <div className="overline mb-2">Rangos IP ({client.ip_ranges?.length || 0})</div>
          {client.ip_ranges?.length ? (
            <div className="space-y-1.5">
              {client.ip_ranges.map((ip) => (
                <div key={ip} className="flex items-center justify-between font-mono text-xs border border-border px-2 py-1.5" data-testid={`ip-${ip}`}>
                  <span className="flex items-center gap-2"><Server className="w-3 h-3 text-primary" />{ip}</span>
                  <button
                    onClick={() => onShodanScan(ip.split("/")[0])}
                    disabled={shodanBusy}
                    className="text-[10px] uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
                    data-testid={`shodan-scan-${ip}`}
                  >
                    <Radar className="w-3 h-3" /> Consulta Shodan
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Aún no hay IPs.</div>
          )}
        </div>
      </div>

      <form onSubmit={addAsset} className="border border-border p-4 bg-background/40 space-y-3">
        <div className="overline">Añadir nuevo activo</div>
        <div className="flex gap-2 border border-border p-1 w-fit">
          {[
            { id: "domain", label: "Dominio", icon: Globe },
            { id: "ip", label: "IP / CIDR", icon: Server },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setAssetKind(t.id)}
              className={cn(
                "flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase px-3 py-1.5",
                assetKind === t.id ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`asset-tab-${t.id}`}
            >
              <t.icon className="w-3 h-3" />{t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={assetVal}
            onChange={(e) => setAssetVal(e.target.value)}
            placeholder={assetKind === "domain" ? "ejemplo.com" : "203.0.113.10 o 198.51.100.0/24"}
            className="flex-1 bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            data-testid="input-asset-value"
          />
          <button
            type="submit"
            disabled={busy || !assetVal.trim()}
            className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="submit-asset"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Añadir
          </button>
        </div>
      </form>
    </section>
  );
}
